import { useState } from "react";
import { Globe, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useLanguage, languages, Language } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { flags } from "@/components/ui/flags";

interface LanguageSwitcherProps {
  variant?: "header" | "footer";
}

export function LanguageSwitcher({ variant = "header" }: LanguageSwitcherProps) {
  const { language, setLanguage } = useLanguage();
  const [open, setOpen] = useState(false);

  const CurrentFlag = flags[language];

  return (
    <DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "gap-2 px-3",
            variant === "header" && "text-foreground hover:text-primary hover:bg-transparent"
          )}
        >
          <CurrentFlag className="w-5 h-4 rounded-sm overflow-hidden" />
          <span className="hidden sm:inline text-sm font-medium">{language.toUpperCase()}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        {languages.map((lang) => {
          const FlagComponent = flags[lang.code];
          return (
            <DropdownMenuItem
              key={lang.code}
              onClick={() => {
                setLanguage(lang.code as Language);
                setOpen(false);
              }}
              className={cn(
                "flex items-center gap-3 cursor-pointer py-2.5",
                language === lang.code && "bg-primary/10 text-primary"
              )}
            >
              <FlagComponent className="w-6 h-4 rounded-sm shadow-sm" />
              <span className="flex-1 font-medium">{lang.nativeName}</span>
              {language === lang.code && (
                <Check className="w-4 h-4 text-primary" />
              )}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
